<script lang="ts" setup>
import { useQueryClient } from '@tanstack/vue-query'
import useVuelidate from '@vuelidate/core'
import {
	alpha,
	helpers,
	maxLength,
	minLength,
	numeric,
	required,
} from '@vuelidate/validators'
import { useDebounceFn } from '@vueuse/core'
import { computed, reactive, ref, watch } from 'vue'
import { GoogleMap, Marker } from 'vue3-google-map'
import { Collapse } from 'vue-collapsed'
import { useToast } from 'vue-toastification'

import AlertComponent from '@/components/AlertComponent.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import ShadowBox from '@/components/ShadowBox.vue'
import StyledButton from '@/components/StyledButton.vue'
import StyledInput from '@/components/StyledInput.vue'

import useAmadeusHotelSearch from '@/internal/modules/providers/composables/useAmadeusHotelSearch.ts'
import useHotels from '@/internal/modules/providers/composables/useHotels'
import type { Hotel } from '@/internal/modules/providers/interfaces/Hotel'

interface Props {
	hotel: Hotel
}

const props = defineProps<Props>()
const queryClient = useQueryClient()
const toast = useToast()

const actionModal = ref(false)
const confirm = ref(false)
const handleActionModal = () => {
	actionModal.value = !actionModal.value
}

const rules = computed(() => ({
	cityCode: {
		required: helpers.withMessage(
			'El codigo de ciudad es requerido',
			required
		),
		minLength: helpers.withMessage(
			'El codigo de ciudad debe de ser 3 caracteres como minimo',
			minLength(3)
		),
		maxLength: helpers.withMessage(
			'El codigo de ciudad debe de ser 3 caracteres como maximo',
			maxLength(3)
		),
		alpha: helpers.withMessage(
			'El codigo solo debe de contener letras',
			alpha
		),
	},
	q: {
		alpha,
	},
}))

const {
	data: amadeusHotels,
	filters,
	cityCode,
	enable,
	isLoading,
	isRefetching,
	remove,
	refetch,
	updateHotelMutation,
	hotelId,
} = useAmadeusHotelSearch({
	queryKey: props.hotel.id.toString(),
	hotelId: props.hotel.id,
})

const loading = computed(
	() => enable.value && (isLoading.value || isRefetching.value)
)

const localHotels = ref()
watch(amadeusHotels, (value) => {
	if (value) {
		localHotels.value = value
	}
})

const v$ = useVuelidate()

const selectedHotel = reactive({
	code: '',
	name: '',
})

const searchHotel = useDebounceFn(async () => {
	const regex = /^[a-zA-Z]+$/

	if (!cityCode.value) {
		toast.error('El codigo de ciudad es requerido')
		return
	}

	if (cityCode.value.length < 3) {
		toast.error('El codigo de ciudad debe de ser 3 caracteres como minimo')
		return
	}

	if (!regex.test(cityCode.value)) {
		toast.error('El codigo de ciudad solo debe de contener letras')
		return
	}

	if (filters.q == '') {
		toast.error('El nombre del hotel es requerido')
		return
	}

	if (!v$.value.$error) {
		enable.value = true
		refetch()
	}
}, 1000)

const handleSetCode = async () => {
	const { code } = selectedHotel
	if (code != '') {
		await updateHotelMutation.mutateAsync(
			{
				hotelId: props.hotel.id,
				code,
			},
			{
				onSuccess: async () => {
					confirm.value = false
					selectedHotel.code = ''
					selectedHotel.name = ''
					remove()
					v$.value.$reset()
					localHotels.value = []
					toast.success('Asignado correctamente')
					await queryClient.invalidateQueries(['hotels', 'list-hotels'])
					await queryClient.refetchQueries(['hotels', 'list-hotels'])
				},
				onError(error, variables, context) {
					toast.error(error as string)
				},
			}
		)
	}
}
</script>

<template>
	<div class="flex items-center gap-1 text-sm" :class="!hotel.amadeusCode ? 'cursor-pointer' : 'cursor-default'"
		@click="
			() => {
				if (!hotel.amadeusCode) {
					handleActionModal()
					hotelId = props.hotel.id
				}
			}
		">
		<span class="material-symbols-outlined text-sm" :class="hotel.amadeusCode ? 'text-green-500' : 'text-red-500'">
			{{ hotel.amadeusCode ? 'check_circle' : 'cancel' }}
		</span>
		{{ hotel.amadeusCode || 'Sin código Amadeus' }}
	</div>

	<ModalComponent :model-value="actionModal">
		<template #modalBody>
			<form class="p-5">
				<div v-if="hotel.amadeusCode"></div>

				<div v-else>
					<div class="flex justify-between w-full gap-2">
						<StyledInput v-model="filters.q" :value="hotel.name" :label="`Nombre del hotel`"
							:placeholder="`Nombre del hotel`" class="w-full" />

						<StyledInput v-model.uppercase="cityCode" :label="`Código de la ciudad`"
							:placeholder="`Código de la ciudad`" class="w-full" :is-required="true" />
					</div>
				</div>

				<div class="overflow-y-scroll h-80 mt-5 snap-y snap-proximity" v-if="localHotels?.length > 0">
					<ShadowBox
						class="group my-3 grid grid-cols-1 md:grid-cols-5 hover:bg-slate-200 transition-all snap-center"
						v-for="hotel in localHotels" :key="hotel.id">
						<div class="h-32 md:w-auto aspect-square">
							<img v-if="hotel.image" class="h-full rounded-lg" :src="hotel.image"
								alt="imagen del hotel" />
							<div v-else class="bg-slate-400 h-full rounded-lg w-full"></div>
						</div>
						<div class="md:pl-10 flex flex-col col-span-3">
							<h3 class="font-bold text-lg">{{ hotel.name }}</h3>
							<p class="text-sm">{{ hotel.chain }}</p>
							<p class="text-sm mt-2">{{ hotel.addressLine }}</p>
						</div>
						<div class="flex items-center justify-center">
							<StyledButton class="bg-blue-500 text-white" @click.prevent="
								() => {
									confirm = true
									selectedHotel.code = hotel.code
									selectedHotel.name = hotel.name
								}
							">Seleccionar hotel</StyledButton>
						</div>
					</ShadowBox>
				</div>

				<div v-if="amadeusHotels?.length == 0"
					class="m-3 relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
					<span class="mt-2 block text-sm font-semibold text-gray-900">No se encontraron resultados hoteles
					</span>
				</div>

				<div class="flex justify-between items-center gap-5 mt-5">
					<StyledButton type="button" class="bg-green-600 hover:bg-green-700 text-white" @click="searchHotel"
						:loading="loading">
						Buscar en Amadeus
					</StyledButton>

					<StyledButton type="button" class="bg-red-500 hover:bg-red-600 text-white" @click.prevent="
						() => {
							localHotels = []
							handleActionModal()
						}
					">
						Cancelar Busqueda
					</StyledButton>
				</div>
			</form>
		</template>
	</ModalComponent>

	<div class="flex items-center justify-center z-50">
		<AlertComponent v-model="confirm" type="warning" buttons dismiss @confirm="handleSetCode"
			:on-cancel="() => (confirm = false)" class="z-50">
			<template v-slot:title>Relacionar hotel con amadeus</template>
			<template v-slot:body>
				se relacionara el hotel {{ selectedHotel.name }} de amadeus con el hotel
				{{ props.hotel.name }}
			</template>
			<template v-slot:closeButton> cancelar </template>
			<template v-slot:button=""> Aceptar </template>
		</AlertComponent>
	</div>
</template>
